import {Component, DestroyRef, effect, inject, input, signal, TemplateRef, viewChild} from '@angular/core';
import {ValuationService} from '../../services/valuation.service';
import {ValuationSessionPageComponent} from './valuation-session-page/valuation-session-page.component';
import {Observable} from 'rxjs';
import type {CompletedSteps} from '../../models/valuation.models';

interface LoadOptions {
  error?: string
}

@Component({template: ''})
export abstract class ValuationTabComponent<T> {
  /** Subclasses set this as a class field (e.g. `protected readonly tabTitle = 'Comparables';`)
   * -- not a constructor param, since Angular can't resolve non-injectable constructor
   * params on a @Component-decorated base class shared across subclasses. */
  protected abstract readonly tabTitle: string;

  readonly sessionId = input.required<string>();
  readonly headerStart = viewChild<TemplateRef<unknown>>('headerStart')
  readonly headerEnd = viewChild<TemplateRef<unknown>>('headerEnd')
  readonly footerStart = viewChild<TemplateRef<unknown>>('footerStart')
  readonly footerEnd = viewChild<TemplateRef<unknown>>('footerEnd')
  readonly actions = viewChild<TemplateRef<unknown>>('actionBar')

  protected readonly valuationService = inject(ValuationService);
  protected readonly sessionPage = inject(ValuationSessionPageComponent);
  protected readonly data = signal<T | null>(null);
  protected abortController: AbortController | null = null;
  protected readonly valid = signal(false);
  protected readonly editing = signal(false);
  protected readonly conditions = signal<Record<string, boolean>>({});

  protected abstract loadData(): Observable<T>;

  constructor() {
    inject(DestroyRef).onDestroy(() => this.abortController?.abort());

    // Deliberately its own effect, separate from the reload effect below:
    // setTabTitle() reads sessionPage.session() internally, so this re-runs
    // once the session finishes loading (to swap in the real address). It
    // must NOT share an effect with the abort-controller/reload logic --
    // sharing one previously meant this same session()-driven re-run aborted
    // the tab's own in-flight data request (via the shared effect's cleanup)
    // moments after a hard page reload, permanently leaving `data` null with
    // nothing left to retry the load.
    effect(() => {
      this.sessionPage.setTabTitle(this.tabTitle);
    });

    effect(() => {
      this.conditions.update(c => ({
        ...c,
        valid: this.valid(),
        notEditing: !this.editing(),
      }))
    })

    effect(() => {
      const conditions = this.conditions();
      this.sessionPage.tabValid.set(Object.entries(conditions).every(([_, v]) => v))
    })

    effect(() => void this.reload())
  }

  protected async reload() {
    this.abortController?.abort();
    this.abortController = new AbortController();
    this.setData(await this.load(this.loadData()));
  }

  /** Sets `data` and, when the response carries one, syncs the session page's
   * server-owned `progress` from it -- every valuation response except the
   * subject-location map Blob carries `progress`, so any load or save routed
   * through here keeps the sidebar's tab-unlocking state fresh without a
   * separate round-trip. `T` isn't constrained to `{ progress }` because a
   * Blob (subject-location's map image) has no properties in common with an
   * all-optional shape and TypeScript's weak-type check rejects it as a
   * generic argument -- checked at runtime here instead. */
  protected setData(value: T | null): void {
    this.data.set(value);
    if (value && typeof value === 'object' && 'progress' in value) {
      this.sessionPage.progress.set((value as { progress: CompletedSteps }).progress);
    }
  }

  protected async load<T2>(op: Observable<T2>, options: LoadOptions = {}): Promise<T2 | null> {
    try {
      return await this.sessionPage.load(op, this.abortController?.signal);
    } catch {
      this.sessionPage.loadError.set(options.error ?? 'Failed to load data.');
      return null;
    }
  }
}
